import Counter from '../../counter/inedx';
import CutDownTimer from '../../cutdown-timer';
import QualityChip from '../../quality-chip';
import tw from 'twin.macro';

// Shared by VideoAndCommentSide (desktop/stacked-mobile, anchor="fixed" against
// the viewport) and the landscape phone layout (exercise/index.jsx renders this
// directly inside the camera card, anchor="absolute" against that card) — same
// counter/timer/quality grouping, positioned against whichever box is actually
// meant to carry it.
const getCounterComponent = ({ inPerformance, isFalseStage, counter, totalReps }) => {
  if (!inPerformance) {
    return null;
  }

  // Show completed / target reps together (e.g. "4/12") so the user sees both
  // how many they've done and how many the set asks for. `counter` counts up
  // from 0 as reps are completed; fall back to just the current count until the
  // backend has told us the set's target rep count.
  //
  // A null `counter` means offline — nothing is counting reps, so the pill shows
  // the prescribed target alone rather than a live count that will never move.
  const hasLiveCounter = counter !== null && counter !== undefined;
  const hasTotal = typeof totalReps === 'number' && totalReps > 0;

  return (
    <Counter
      counterCurrentValue={hasLiveCounter ? counter || 0 : null}
      counterTotal={hasTotal ? totalReps : null}
      isFalseStage={isFalseStage}
    />
  );
};

const PerformanceReadoutOverlay = ({
  anchor = 'fixed',
  inPerformance,
  isFalseStage,
  counter,
  totalReps,
  shouldShowCounter,
  shouldShowTimer,
  isTimedReps,
  timerValue,
  exerciseType,
  showQuality,
  currentScore,
  hasQualityScore,
  hasFpsOverlay = false,
}) => {
  const positionStyle = anchor === 'absolute' ? tw`absolute` : tw`fixed`;

  return (
    <>
      {/* Top-left: the rep counter for rep-based modes, plus — for pure Fixed
          Time — the countdown timer, which lives at the left with its icon
          (matches the "Time-based holds" design).
          dir="ltr": this is a video overlay control, not reading-direction
          content — without it, Arabic flips the column's cross-axis start
          from left to right, relocating the whole stack under the top-right
          camera/menu icons instead of leaving it pinned at top-left. */}
      <div
        dir="ltr"
        tw="items-start flex flex-col gap-2 z-[100] top-0 left-0 px-4 pt-4"
        css={positionStyle}
      >
        {shouldShowCounter &&
          getCounterComponent({
            inPerformance,
            isFalseStage,
            counter,
            totalReps,
          })}

        {/* TIMED_REPS takes the countdown out of this stack on md and up — see
            below. Every other type that shows one keeps it here, under the
            counter. Mobile keeps TIMED_REPS here too (md:hidden): the
            separately-centered chip below lands close enough to the top-right
            camera/menu group on a narrow screen to collide with it, so mobile
            instead stacks it under the counter like every other type. */}
        {shouldShowTimer && isTimedReps && (
          <div tw="md:hidden">
            <CutDownTimer timer={timerValue} exerciseType={exerciseType} />
          </div>
        )}
        {shouldShowTimer && !isTimedReps && (
          <CutDownTimer timer={timerValue} exerciseType={exerciseType} />
        )}

        {/* Live quality/score, shown as a percentage under the counter (rep
            modes) or under the timer (Fixed Time). */}
        {showQuality && <QualityChip score={currentScore} hasScore={hasQualityScore} />}
      </div>

      {/* TIMED_REPS ("rep based with timer"), md and up: the clock is what ends
          the set while the reps still count, so the design gives it its own
          chip at the top centre — reps and score stay stacked in the corner,
          and the countdown reads as the set's own deadline rather than a third
          value in that column. Dropped to 84px only while the FPS debug pill
          (fixed, top-4) is actually showing, so this chip ducks under it
          instead of overlapping — flush at the top (matching the counter
          stack's own pt-4) the rest of the time. */}
      {shouldShowTimer && isTimedReps && (
        <div
          dir="ltr"
          tw="pointer-events-none inset-x-0 z-[100] hidden md:flex justify-center"
          css={[positionStyle, hasFpsOverlay ? tw`top-[84px]` : tw`top-4`]}
        >
          <CutDownTimer timer={timerValue} exerciseType={exerciseType} hideIcon />
        </div>
      )}
    </>
  );
};

export default PerformanceReadoutOverlay;
